import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { COMPONENTI, STATI_RECLAMO, eur, today } from "../lib/format";
import { TextField, SelectField, Field } from "./Field";
import { AnalisiAI } from "./AnalisiAI";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const EMPTY = { garanzia_id: "", data_apertura: today(), km: 0, componente: "", descrizione: "", stato: "aperto", officina: "", costo_ricambi: 0, costo_manodopera: 0, data_chiusura: "", note: "" };
const NUM = ["km", "costo_ricambi", "costo_manodopera"];

export const ReclamoDialog = ({ open, onClose, initial, garanziaId, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : { ...EMPTY, garanzia_id: garanziaId }); }, [open, initial, garanziaId]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, NUM.includes(k) ? Number(f[k]) || 0 : f[k]]));
      if (["chiuso", "respinto"].includes(payload.stato) && !payload.data_chiusura) payload.data_chiusura = today();
      initial?.id ? await api.put(`/reclami/${initial.id}`, payload) : await api.post("/reclami", payload);
      toast.success("Reclamo salvato"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="reclamo-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? `Reclamo ${initial.numero}` : "Nuovo reclamo / intervento"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Data apertura" type="date" value={f.data_apertura} onChange={set("data_apertura")} testId="reclamo-data-input" />
          <TextField label="Km al guasto" type="number" value={f.km} onChange={set("km")} testId="reclamo-km-input" />
          <SelectField label="Componente *" value={f.componente} onChange={set("componente")} testId="reclamo-componente-select"
            options={[...COMPONENTI, "Altro"].map((c) => ({ value: c, label: c }))} />
          <SelectField label="Stato" value={f.stato} onChange={set("stato")} testId="reclamo-stato-select"
            options={Object.entries(STATI_RECLAMO).map(([value, label]) => ({ value, label }))} />
          <Field label="Descrizione del difetto *" className="sm:col-span-2">
            <Textarea value={f.descrizione} onChange={(e) => set("descrizione")(e.target.value)} data-testid="reclamo-descrizione-input" />
          </Field>
          <AnalisiAI garanziaId={f.garanzia_id} descrizione={f.descrizione} km={f.km} onApply={(r) => setF((p) => ({
            ...p, componente: r.componente, costo_ricambi: r.stima_ricambi, costo_manodopera: r.stima_manodopera,
            note: [p.note, `Analisi AI: ${r.motivazione}`].filter(Boolean).join("\n"),
          }))} />
          <TextField label="Officina" value={f.officina} onChange={set("officina")} testId="reclamo-officina-input" className="sm:col-span-2" />
          <TextField label="Costo ricambi (€)" type="number" value={f.costo_ricambi} onChange={set("costo_ricambi")} testId="reclamo-ricambi-input" />
          <TextField label="Costo manodopera (€)" type="number" value={f.costo_manodopera} onChange={set("costo_manodopera")} testId="reclamo-manodopera-input" />
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm sm:col-span-2">Totale intervento: <b data-testid="reclamo-totale">{eur(Number(f.costo_ricambi || 0) + Number(f.costo_manodopera || 0))}</b></div>
          <TextField label="Data chiusura" type="date" value={f.data_chiusura} onChange={set("data_chiusura")} testId="reclamo-chiusura-input" />
          <Field label="Note" className="sm:col-span-2"><Textarea value={f.note} onChange={(e) => set("note")(e.target.value)} data-testid="reclamo-note-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reclamo-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={!f.componente || !f.descrizione} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="reclamo-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
